import {useCallback, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Flex, Input, Text} from '@chakra-ui/react'

import {UploadIcon} from '../../icons/UploadIcon'

const FileUpload = ({
  value,
  onChange,
  onRemove,
  error,
  prompt,
  subtext,
  accept,
  allowedTypes,
  typeErrorMessage,
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [typeError, setTypeError] = useState(null)
  const inputRef = useRef(null)

  const handleFileSelected = useCallback(
    (file) => {
      if (!file) return

      if (allowedTypes && !allowedTypes.includes(file.type)) {
        setTypeError(typeErrorMessage)
        if (inputRef.current) {
          inputRef.current.value = ''
        }
        return
      }

      setTypeError(null)
      onChange(file)
    },
    [onChange, allowedTypes, typeErrorMessage],
  )

  const handleFileInputChange = useCallback(
    (event) => {
      const [file] = event.target.files
      handleFileSelected(file)
    },
    [handleFileSelected],
  )

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault()
      setIsDragging(false)
      const [file] = event.dataTransfer.files
      handleFileSelected(file)
    },
    [handleFileSelected],
  )

  const handleRemove = useCallback(() => {
    setTypeError(null)
    onRemove()
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }, [onRemove])

  return (
    <Box>
      {value ? (
        <Flex
          align="center"
          justify="space-between"
          border="1px solid"
          borderColor="mld.neutral.500"
          borderRadius="md"
          p={3}
        >
          <Text fontSize="sm" fontWeight="medium" noOfLines={1} flex={1} mr={2}>
            {value.name}
          </Text>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            aria-label="Remove file"
            _hover={{}}
          >
            ✕
          </Button>
        </Flex>
      ) : (
        <Box
          data-testid="file-upload-zone"
          textAlign="center"
          borderColor={isDragging ? 'mld.primary.100' : 'mld.neutral.500'}
          borderWidth="1px"
          borderRadius="md"
          p={6}
          cursor="pointer"
          onClick={() => inputRef.current.click()}
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault()
            setIsDragging(true)
          }}
          onDragLeave={(e) => {
            e.preventDefault()
            setIsDragging(false)
          }}
        >
          <Flex direction="column" align="center" gap={2}>
            <UploadIcon boxSize="6" />
            <Text fontSize="sm" fontWeight="medium" lineHeight="125%">
              {prompt}
            </Text>
            <Text fontSize="xs" color="mld.neutral.700">
              {subtext}
            </Text>
          </Flex>
        </Box>
      )}

      <Input
        hidden
        type="file"
        accept={accept}
        ref={inputRef}
        onChange={handleFileInputChange}
      />

      {(error || typeError) && (
        <Text mt={1} fontSize="sm" color="red.500">
          {typeError || error}
        </Text>
      )}
    </Box>
  )
}

FileUpload.propTypes = {
  value: PropTypes.instanceOf(File),
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  error: PropTypes.string,
  prompt: PropTypes.string.isRequired,
  subtext: PropTypes.string.isRequired,
  accept: PropTypes.string,
  allowedTypes: PropTypes.arrayOf(PropTypes.string),
  typeErrorMessage: PropTypes.string,
}

FileUpload.defaultProps = {
  value: null,
  error: null,
  accept: undefined,
  allowedTypes: undefined,
  typeErrorMessage: undefined,
}

export default FileUpload
